import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Form } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { MyFromTextarea } from "@/components/zthd/MyFormField";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { zodResolver } from "@hookform/resolvers/zod";
import { PlusIcon } from "lucide-react";
import { mutateRequest, postFetcher } from "@/lib/swr";
import { useApps } from "@/hooks/apps";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import useSWRMutation from "swr/mutation";
import { z } from "zod";
import type { App, IssueType } from "@/types/domain";

const formSchema = z.object({
  doc: z
    .string()
    .min(1, "Doc link is required")
    .regex(
      /^https:\/\/stickerstyle\.feishu\.cn\/(docx|wiki)\/[A-Za-z0-9_-]+$/,
      "Doc link must be a valid Feishu doc link (https://stickerstyle.feishu.cn/docx/xxx or /wiki/xxx)"
    )
    .refine((value) => !value.includes("?"), "Doc link cannot contain '?'")
    .transform((str) => str.trim()),
});

type Mode = "feishu" | "native";

const TYPE_LABELS: Record<IssueType, string> = {
  prd: "PRD",
  bug: "Bug",
  doc: "Doc",
  todo: "Todo",
};

// Todos are follow-ups split off a PRD and must hang under one (WED-148);
// this dialog has no parent picker, so it doesn't offer them.
const CREATABLE_TYPES: IssueType[] = ["prd", "bug", "doc"];

type SubmitPrd = (payload: Record<string, unknown>) => Promise<unknown>;

// Regular PRD: link a Feishu doc. RHF + zod; owns its state — DialogContent
// unmounts on close, so a reopened dialog starts fresh.
const CreatePrdFeishuForm = ({
  submit,
  isMutating,
  canSubmit,
}: {
  submit: SubmitPrd;
  isMutating: boolean;
  canSubmit: boolean;
}) => {
  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: { doc: "" },
  });

  // Land the cursor straight in the doc input (the dialog's own autofocus is
  // suppressed — Radix would focus the first tab trigger).
  useEffect(() => {
    form.setFocus("doc");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = (values: z.infer<typeof formSchema>) => submit(values);

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <div className="flex flex-col space-y-2">
          <ul className="text-destructive list-disc pl-4">
            <li>Accept Feishu doc.</li>
            <li>https://stickerstyle.feishu.cn/docx/xxx or /wiki/xxx</li>
            <li>not contain &apos;from_copylink&apos;</li>
          </ul>
          <MyFromTextarea
            name="doc"
            form={form}
            placeholder="feishu doc link"
            rows={3}
          />
        </div>
        <div className="flex justify-end">
          <Button
            type="submit"
            disabled={form.formState.isSubmitting || !canSubmit}
          >
            {isMutating ? "Creating…" : "Create"}
          </Button>
        </div>
      </form>
    </Form>
  );
};

// Native lightweight PRD (issue/task-like): markdown written directly.
const CreatePrdNativeForm = ({
  submit,
  isMutating,
  canSubmit,
}: {
  submit: SubmitPrd;
  isMutating: boolean;
  canSubmit: boolean;
}) => {
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const titleRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    titleRef.current?.focus();
  }, []);

  const onSubmit = () =>
    submit({ title: title.trim(), content: content || undefined });

  return (
    <div className="space-y-4">
      <Input
        ref={titleRef}
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Title"
      />
      <Textarea
        value={content}
        onChange={(e) => setContent(e.target.value)}
        placeholder="Content (markdown, optional — you can also write it later on the detail page)"
        rows={8}
        className="font-mono text-xs leading-relaxed"
      />
      <div className="flex justify-end">
        <Button
          type="button"
          onClick={onSubmit}
          disabled={!title.trim() || isMutating || !canSubmit}
        >
          {isMutating ? "Creating…" : "Create"}
        </Button>
      </div>
    </div>
  );
};

// `appId`: when creating from an app's PRDs page, the new PRD relates to that
// app (lands in its pool). Entries without app context (My PRDs / All PRDs)
// must pick one — every PRD is required to land in some app's pool, or it's
// invisible to everyone but its owner. Two modes: link a Feishu doc (regular
// PRD), or write markdown directly (native lightweight PRD, issue/task-like);
// the shell owns the dialog/tabs/app picker, each mode owns its own form.
const CreatePrd = ({ appId }: { appId?: string }) => {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<Mode>("feishu");
  // Issue unification: one create entry for all issue kinds — the kind is a
  // radio in the dialog (prd default), not separate buttons. The version
  // page's SubmitBugs stays: that entry carries provenance (sourceVersionId).
  const [issueType, setIssueType] = useState<IssueType>("prd");
  const appsData = useApps();
  const apps: App[] = Array.isArray(appsData) ? appsData : [];
  const [selectedApp, setSelectedApp] = useState("");
  const effectiveAppId = appId ?? (selectedApp || undefined);
  const { trigger, isMutating } = useSWRMutation("/prds", postFetcher, {
    onSuccess: () => {
      mutateRequest("/prds");
      setOpen(false);
    },
  });

  const submit: SubmitPrd = (payload) =>
    trigger({ ...payload, type: issueType, appId: effectiveAppId });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="h-8">
          <PlusIcon className="w-4 h-4 mr-2" /> New PRD
        </Button>
      </DialogTrigger>
      <DialogContent onOpenAutoFocus={(e: Event) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>Create {TYPE_LABELS[issueType]}</DialogTitle>
          <DialogDescription>
            Link a Feishu doc, or write markdown directly.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-3">
          <span className="text-sm text-muted-foreground">Type</span>
          <RadioGroup
            className="flex items-center gap-4"
            value={issueType}
            onValueChange={(v) => setIssueType(v as IssueType)}
          >
            {CREATABLE_TYPES.map((t) => (
              <div key={t} className="flex items-center gap-1.5">
                <RadioGroupItem value={t} id={`issue-type-${t}`} />
                <Label htmlFor={`issue-type-${t}`} className="text-sm">
                  {TYPE_LABELS[t]}
                </Label>
              </div>
            ))}
          </RadioGroup>
        </div>

        <Tabs value={mode} onValueChange={(v) => setMode(v as Mode)}>
          <TabsList>
            <TabsTrigger value="feishu">Feishu doc</TabsTrigger>
            <TabsTrigger value="native">Write markdown</TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Required when there's no app context from the entry point */}
        {!appId && (
          <div className="space-y-1">
            <span className="text-sm text-muted-foreground">
              App (required)
            </span>
            <Select value={selectedApp} onValueChange={setSelectedApp}>
              <SelectTrigger size="sm" className="w-full">
                <SelectValue placeholder="Pick the app this PRD targets" />
              </SelectTrigger>
              <SelectContent>
                {apps.map((a) => (
                  <SelectItem key={a.id} value={String(a.id)}>
                    {a.id} - {a.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {mode === "feishu" ? (
          <CreatePrdFeishuForm
            submit={submit}
            isMutating={isMutating}
            canSubmit={!!effectiveAppId}
          />
        ) : (
          <CreatePrdNativeForm
            submit={submit}
            isMutating={isMutating}
            canSubmit={!!effectiveAppId}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CreatePrd;
